import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatBRL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sun, Sunset, Moon, Target, Cake, Clock, Zap, TrendingUp, ArrowRight, Sparkles } from "lucide-react";

/**
 * Popup "Resumo do dia" — aparece 1x por dia por usuário.
 * Cenário A: 100% templates estáticos com variáveis dinâmicas (zero custo LLM).
 * Regras:
 *  - Aparece se hoje ainda não foi mostrado (chave localStorage: resumo-dia-<userId>-<YYYY-MM-DD>).
 *  - Mostra: saudação por horário + retornos atrasados/hoje + aniversários + % meta.
 *  - Mensagem motivacional muda conforme a % da meta.
 */
export default function ResumoDoDiaModal({ user }) {
  const [open, setOpen] = useState(false);
  const [dados, setDados] = useState(null);
  const nav = useNavigate();

  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const chave = user ? `resumo-dia-${user.id}-${hoje}` : null;

  useEffect(() => {
    if (!user || !chave) return;
    if (localStorage.getItem(chave)) return; // já viu hoje
    // Só mostra p/ Vendedora e Diretoria (Fiscal/Supervisão podem ver também sem prejuízo)
    if (!["vendedora", "diretoria", "supervisao"].includes(user.role)) return;
    // Busca dados em paralelo
    Promise.all([
      api.get("/agenda/retornos").catch(() => ({ data: { atrasados: [], hoje: [] } })),
      api.get("/agenda/aniversarios").catch(() => ({ data: { hoje: [] } })),
      api.get("/dashboards/meta-usuario").catch(() => ({ data: null })),
    ]).then(([r, a, m]) => {
      setDados({
        atrasados: r.data?.atrasados || [],
        hojeRet: r.data?.hoje || [],
        aniversarios: a.data?.hoje || [],
        meta: m.data,
      });
      setOpen(true);
    });
  }, [user, chave]);

  const fechar = () => {
    if (chave) localStorage.setItem(chave, "1");
    setOpen(false);
  };

  if (!dados) return null;

  const hora = new Date().getHours();
  const saudacao = hora < 12 ? { txt: "Bom dia", Ico: Sun, cor: "text-amber-500" }
    : hora < 18 ? { txt: "Boa tarde", Ico: Sunset, cor: "text-orange-500" }
    : { txt: "Boa noite", Ico: Moon, cor: "text-indigo-500" };

  const primeiroNome = (user?.nome || "").split(" ")[0] || "";
  const totalAtrasados = dados.atrasados.length;
  const totalHoje = dados.hojeRet.length;
  const totalAniv = dados.aniversarios.length;
  const meta = dados.meta || {};
  const pct = Math.round(meta.percentual || 0);
  const falta = meta.falta || 0;
  const semMeta = !!meta.sem_meta;

  // Mensagem motivacional por faixa de meta
  let motivacao = null;
  if (semMeta) {
    motivacao = {
      titulo: "⚠️ Meta do mês ainda não cadastrada",
      texto: "Fale com a Diretoria para cadastrar sua meta. Sem meta, o placar do dia não faz sentido.",
      cor: "bg-amber-50 border-amber-300 text-amber-900",
    };
  } else if (pct >= 100) {
    motivacao = {
      titulo: "🏆 Meta batida! Você é fera.",
      texto: "Agora o jogo é bater seu recorde. Cada venda daqui pra frente é medalha extra.",
      cor: "bg-emerald-50 border-emerald-300 text-emerald-900",
    };
  } else if (pct >= 90) {
    motivacao = {
      titulo: "🔥 Você está a UM PASSO da meta!",
      texto: `Faltam apenas ${formatBRL(falta)}. Um único fechamento certo hoje e você bate.`,
      cor: "bg-emerald-50 border-emerald-300 text-emerald-900",
    };
  } else if (pct >= 70) {
    motivacao = {
      titulo: "💪 Reta final! 70%+ já é você.",
      texto: `Faltam ${formatBRL(falta)}. Foca nos 3 municípios mais quentes e não solta.`,
      cor: "bg-blue-50 border-blue-300 text-blue-900",
    };
  } else if (pct >= 50) {
    motivacao = {
      titulo: "⚡ Metade batida — segunda metade é sua!",
      texto: `Faltam ${formatBRL(falta)}. Vamos escalar: 1 ligação a mais por dia muda o placar.`,
      cor: "bg-blue-50 border-blue-300 text-blue-900",
    };
  } else if (pct >= 25) {
    motivacao = {
      titulo: "🚀 Bora acelerar!",
      texto: `Faltam ${formatBRL(falta)}. Prospecção ativa hoje = venda amanhã. Mãos à obra.`,
      cor: "bg-amber-50 border-amber-300 text-amber-900",
    };
  } else {
    motivacao = {
      titulo: "⏰ O dia é uma nova chance.",
      texto: `Ainda são ${formatBRL(falta)} até a meta. Comece pelos retornos — cada follow-up conta.`,
      cor: "bg-red-50 border-red-300 text-red-900",
    };
  }

  const irAgenda = () => { fechar(); nav("/agenda"); };
  const irCarteira = () => { fechar(); nav("/carteira"); };
  const irDashboard = () => { fechar(); nav("/geral"); };

  const Ico = saudacao.Ico;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-lg" data-testid="modal-resumo-do-dia">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Ico className={`w-6 h-6 ${saudacao.cor}`} />
            {saudacao.txt}, <span className="text-blue-600">{primeiroNome}</span>!
          </DialogTitle>
          <DialogDescription>
            Seu resumo de hoje · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
          </DialogDescription>
        </DialogHeader>

        <div className={`p-4 rounded-xl border-2 ${motivacao.cor}`} data-testid="resumo-motivacao">
          <div className="font-display font-bold text-lg mb-1">{motivacao.titulo}</div>
          <div className="text-sm">{motivacao.texto}</div>
        </div>

        {!semMeta && (
          <div className="grid grid-cols-3 gap-2 my-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Meta do mês</div>
              <div className="text-2xl font-black tabular-nums text-slate-900">{pct}%</div>
              <div className="text-[10px] text-slate-500">de {formatBRL(meta.meta || 0)}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Faltam</div>
              <div className="text-2xl font-black tabular-nums text-red-600">{formatBRL(falta)}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Realizado</div>
              <div className="text-2xl font-black tabular-nums text-emerald-600">{formatBRL(meta.realizado || 0)}</div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {totalAtrasados > 0 && (
            <button
              type="button"
              onClick={irAgenda}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 transition"
              data-testid="resumo-atrasados"
            >
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-red-600" />
                <b className="text-red-800">{totalAtrasados}</b>
                <span className="text-red-700">retorno{totalAtrasados > 1 ? "s" : ""} <b>atrasado{totalAtrasados > 1 ? "s" : ""}</b> — resolve isso primeiro!</span>
              </div>
              <ArrowRight className="w-4 h-4 text-red-600" />
            </button>
          )}
          {totalHoje > 0 && (
            <button
              type="button"
              onClick={irAgenda}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200 hover:bg-blue-100 transition"
              data-testid="resumo-hoje"
            >
              <div className="flex items-center gap-2 text-sm">
                <Zap className="w-4 h-4 text-blue-600" />
                <b className="text-blue-800">{totalHoje}</b>
                <span className="text-blue-700">retorno{totalHoje > 1 ? "s" : ""} agendado{totalHoje > 1 ? "s" : ""} para hoje</span>
              </div>
              <ArrowRight className="w-4 h-4 text-blue-600" />
            </button>
          )}
          {totalAniv > 0 && (
            <button
              type="button"
              onClick={irAgenda}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition"
              data-testid="resumo-aniversarios"
            >
              <div className="flex items-center gap-2 text-sm">
                <Cake className="w-4 h-4 text-amber-600" />
                <b className="text-amber-800">{totalAniv}</b>
                <span className="text-amber-700">aniversariante{totalAniv > 1 ? "s" : ""} hoje — mande um parabéns e fortaleça o vínculo!</span>
              </div>
              <ArrowRight className="w-4 h-4 text-amber-600" />
            </button>
          )}
          {totalAtrasados === 0 && totalHoje === 0 && totalAniv === 0 && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2" data-testid="resumo-limpo">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              Nenhuma pendência na Agenda. Bora prospectar e abrir novos ciclos!
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={fechar} className="flex-1" data-testid="resumo-fechar">
            Fechar
          </Button>
          <Button onClick={irDashboard} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="resumo-dashboard">
            <TrendingUp className="w-4 h-4 mr-1" /> Ver dashboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
